import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import Newsletter from "@/components/Newsletter";
import { getBlogPost } from "@/lib/api";
import { ASSETS } from "@/lib/assets";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null); setNotFound(false);
    getBlogPost(slug).then(setPost).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="blog-post-notfound" className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
        <h1 className="font-display uppercase text-5xl">Article Not Found</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 text-wutn font-cond uppercase tracking-wider text-sm"><ArrowLeft className="h-4 w-4" /> Back to Journal</Link>
      </div>
    );
  }

  if (!post) {
    return <div className="min-h-[70vh] flex items-center justify-center"><div className="h-10 w-10 border-2 border-wutn border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="blog-post-page">
      <section className="relative pt-40 pb-16 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 z-0">
          <img src={ASSETS.preachingStage} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/50" />
        </div>
        <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8">
          <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-2 text-zinc-400 hover:text-wutn font-cond uppercase tracking-wider text-sm transition-colors"><ArrowLeft className="h-4 w-4" /> Journal</Link>
          <span className="mt-8 block text-xs uppercase tracking-[0.25em] text-wutn font-bold">{post.category}</span>
          <h1 className="mt-4 font-display uppercase text-4xl md:text-6xl leading-[0.9]">{post.title}</h1>
          <p className="mt-6 text-sm text-zinc-500">{post.author} · {new Date(post.date).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })} · {post.read_time}</p>
        </div>
      </section>

      <article className="bg-black py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <div className="space-y-6 text-zinc-300 text-lg leading-relaxed">
              {post.content.split("\n\n").map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </Reveal>

          <div className="mt-16 bg-[#0A0A0A] border border-white/10 p-8">
            <h3 className="font-display uppercase text-2xl">Never Miss A Word</h3>
            <p className="mt-2 text-zinc-400 text-sm mb-5">Get new articles and sermons in your inbox.</p>
            <Newsletter />
          </div>

          <div className="mt-10 flex justify-between">
            <Link to="/blog" className="inline-flex items-center gap-2 text-white hover:text-wutn font-cond uppercase tracking-wider text-sm transition-colors"><ArrowLeft className="h-4 w-4" /> All Articles</Link>
            <Link to="/blog" className="inline-flex items-center gap-2 text-white hover:text-wutn font-cond uppercase tracking-wider text-sm transition-colors">More Articles <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </article>
    </div>
  );
}
